/**
 * V2 onboarding UI primitives — component exports only.
 * Tokens (V2C, font constants) live in tokens.ts to satisfy Fast Refresh.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { V2C, fontBody, fontDisplay } from './tokens';

// ── StatusKind (type exported alongside components — acceptable) ──────────────
export type StatusKind = 'none' | 'connecting' | 'connected' | 'pending' | 'error' | 'skipped';

// ── V2Label ───────────────────────────────────────────────────────────────────
interface V2LabelProps {
  children: React.ReactNode;
  optional?: boolean;
  className?: string;
  /** When set, renders a real <label> bound to that control instead of a <div>. */
  htmlFor?: string;
}
export function V2Label({ children, optional, className, htmlFor }: V2LabelProps) {
  const Tag = htmlFor ? 'label' : 'div';
  return (
    <Tag
      htmlFor={htmlFor}
      className={cn('text-[11px] font-bold uppercase tracking-[0.8px] mb-1.5', className)}
      style={{ color: V2C.mute, fontFamily: fontBody, display: 'block' }}
    >
      {children}
      {optional && (
        <span style={{ color: V2C.muteSoft, fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginLeft: 4 }}>
          · optional
        </span>
      )}
    </Tag>
  );
}

// ── V2InlineMsg ───────────────────────────────────────────────────────────────
type MsgKind = 'error' | 'success' | 'hint';
export function V2InlineMsg({ kind, children }: { kind: MsgKind; children: React.ReactNode }) {
  const color = kind === 'error' ? V2C.err : kind === 'success' ? V2C.ok : V2C.mute;
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color, fontWeight: kind === 'hint' ? 500 : 600, fontFamily: fontBody }}
    >
      {kind === 'error' && <ExclamationIcon size={12} color={color} />}
      {kind === 'success' && <CheckIcon size={12} color={color} />}
      {children}
    </div>
  );
}

// ── V2Input ───────────────────────────────────────────────────────────────────
interface V2InputProps {
  label?: string;
  optional?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  error?: string | null;
  success?: string | null;
  hint?: string | null;
  autoComplete?: string;
  autoFocus?: boolean;
  name?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  /** Stable analytics identifier — rendered as data-onboarding-field. */
  onboardingField?: string;
}
export function V2Input({
  label, optional, value, onChange, placeholder, type = 'text',
  prefix, suffix, error, success, hint, autoComplete, autoFocus, name, disabled, style, inputStyle,
  onboardingField,
}: V2InputProps) {
  const [focused, setFocused] = React.useState(false);
  const inputId = React.useId();
  const ringColor = error ? V2C.err : success ? V2C.ok : V2C.brand;
  const borderColor = error ? V2C.errSoft : success ? V2C.okSoft : V2C.line;
  return (
    <div style={{ marginBottom: 14, ...style }}>
      {label && <V2Label optional={optional} htmlFor={inputId}>{label}</V2Label>}
      <div style={{
        background: disabled ? V2C.bgAlt : '#fff',
        borderRadius: 12,
        border: `1.5px solid ${focused ? ringColor : borderColor}`,
        padding: '0 14px',
        display: 'flex', alignItems: 'center', gap: 8,
        boxShadow: focused ? `0 0 0 3px ${error ? V2C.errBg : success ? V2C.okBg : V2C.brandSoft}` : 'none',
        transition: 'all 0.18s ease',
      }}>
        {prefix && <span style={{ fontSize: 14, color: V2C.mute, flexShrink: 0 }}>{prefix}</span>}
        <input
          id={inputId}
          type={type} value={value} disabled={disabled}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder} autoComplete={autoComplete}
          autoFocus={autoFocus} name={name}
          data-onboarding-field={onboardingField}
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none',
            background: 'transparent',
            fontFamily: fontBody, fontSize: 15, fontWeight: 500,
            color: disabled ? V2C.mute : V2C.ink,
            padding: '13px 0',
            ...inputStyle,
          }}
        />
        {suffix}
      </div>
      {error   && <V2InlineMsg kind="error">{error}</V2InlineMsg>}
      {!error && success && <V2InlineMsg kind="success">{success}</V2InlineMsg>}
      {!error && !success && hint && <V2InlineMsg kind="hint">{hint}</V2InlineMsg>}
    </div>
  );
}

// ── V2Textarea ────────────────────────────────────────────────────────────────
interface V2TextareaProps {
  label?: string;
  optional?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** Stable analytics identifier — rendered as data-onboarding-field. */
  onboardingField?: string;
}
export function V2Textarea({ label, optional, value, onChange, placeholder, maxLength, rows = 3, onboardingField }: V2TextareaProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <V2Label optional={optional}>{label}</V2Label>}
      <textarea
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} maxLength={maxLength} rows={rows}
        data-onboarding-field={onboardingField}
        style={{
          width: '100%', resize: 'vertical', boxSizing: 'border-box',
          background: '#fff', border: `1.5px solid ${V2C.line}`, borderRadius: 12,
          padding: 12, fontFamily: fontBody, fontSize: 14, color: V2C.ink,
          outline: 'none', lineHeight: 1.5,
        }}
      />
      {maxLength && (
        <div style={{ fontSize: 11, color: V2C.mute, textAlign: 'right', marginTop: 2 }}>
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
}

// ── V2Select ──────────────────────────────────────────────────────────────────
interface V2SelectProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  error?: string | null;
  /** Stable analytics identifier — rendered as data-onboarding-field. */
  onboardingField?: string;
}
export function V2Select({ label, value, onChange, options, error, onboardingField }: V2SelectProps) {
  const selectId = React.useId();
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <V2Label htmlFor={selectId}>{label}</V2Label>}
      <select
        id={selectId}
        value={value} onChange={e => onChange(e.target.value)}
        data-onboarding-field={onboardingField}
        style={{
          width: '100%', padding: '13px 14px', borderRadius: 12,
          border: `1.5px solid ${error ? V2C.errSoft : V2C.line}`,
          background: '#fff', fontFamily: fontBody, fontSize: 15, color: V2C.ink,
          outline: 'none', appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%236B6A8A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 14px center',
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <V2InlineMsg kind="error">{error}</V2InlineMsg>}
    </div>
  );
}

// ── V2Btn ─────────────────────────────────────────────────────────────────────
interface V2BtnProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  secondary?: boolean;
  danger?: boolean;
  small?: boolean;
  loading?: boolean;
  style?: React.CSSProperties;
  type?: 'button' | 'submit';
}
export function V2Btn({ children, onClick, disabled, secondary, danger, small, loading, style, type = 'button' }: V2BtnProps) {
  const [pressed, setPressed] = React.useState(false);
  const isDisabled = disabled || loading;
  const bg = isDisabled ? V2C.brandSoft
    : danger    ? '#FFF'
    : secondary ? '#fff'
    : V2C.brand;
  const fg = isDisabled ? '#B5B4D8'
    : danger    ? V2C.err
    : secondary ? V2C.ink
    : '#fff';
  const shadow = secondary || danger
    ? `inset 0 0 0 1px ${danger ? V2C.errSoft : V2C.lineHard}`
    : pressed
      ? '0 2px 6px rgba(89,86,246,0.18)'
      : '0 8px 20px rgba(89,86,246,0.26), 0 4px 0 #3835C2';

  return (
    // aria-disabled instead of the native disabled attribute: disabled form
    // controls swallow pointer events in some browsers, which would make
    // blocked-CTA attempts invisible to onboarding telemetry. The onClick
    // guard keeps the button inert while unavailable.
    <button
      type={type}
      onMouseDown={() => !isDisabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onClick?.();
      }}
      aria-disabled={isDisabled || undefined}
      style={{
        width: '100%', height: small ? 44 : 52, borderRadius: 14, border: 'none',
        background: bg, color: fg, boxShadow: shadow,
        fontFamily: fontBody, fontSize: small ? 14 : 16, fontWeight: 600,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transform: pressed ? 'translateY(2px) scale(0.98)' : 'translateY(0) scale(1)',
        transition: pressed
          ? 'transform 0.08s ease, box-shadow 0.08s ease'
          : 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease',
        ...style,
      }}
    >
      {loading ? <SpinnerIcon size={18} color={fg} /> : children}
    </button>
  );
}

export function V2GhostBtn({ children, onClick, style }: { children: React.ReactNode; onClick?: () => void; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '12px', borderRadius: 12, border: 'none',
        background: 'transparent', color: V2C.mute,
        fontFamily: fontBody, fontSize: 13, fontWeight: 500, cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── V2SectionWarning ──────────────────────────────────────────────────────────
export function V2SectionWarning({ title, children, action }: {
  title: string; children?: React.ReactNode; action?: React.ReactNode
}) {
  return (
    <div style={{
      background: V2C.warnBg, borderRadius: 14, border: `1px solid ${V2C.warnSoft}`,
      padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        border: `1px solid ${V2C.warnSoft}`,
      }}>
        <ExclamationIcon size={16} color={V2C.warn} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: V2C.warn, marginBottom: 2 }}>{title}</div>
        {children && <div style={{ fontSize: 13, color: V2C.warn, opacity: 0.9, lineHeight: 1.45 }}>{children}</div>}
        {action && <div style={{ marginTop: 8 }}>{action}</div>}
      </div>
    </div>
  );
}

// ── V2StatusCard ──────────────────────────────────────────────────────────────
const statusMap: Record<StatusKind, { bg: string; border: string; pillBg: string; pillFg: string; label: string }> = {
  none:       { bg: '#fff',       border: V2C.line,      pillBg: V2C.bgAlt,    pillFg: V2C.mute,  label: 'Not connected' },
  connected:  { bg: '#fff',       border: V2C.okSoft,    pillBg: V2C.okBg,     pillFg: V2C.ok,    label: '✓ Connected' },
  pending:    { bg: '#fff',       border: V2C.warnSoft,  pillBg: V2C.warnBg,   pillFg: V2C.warn,  label: 'Needs attention' },
  error:      { bg: '#fff',       border: V2C.errSoft,   pillBg: V2C.errBg,    pillFg: V2C.err,   label: 'Connection failed' },
  skipped:    { bg: V2C.bgAlt,    border: V2C.line,      pillBg: '#fff',       pillFg: V2C.mute,  label: 'Skipped for now' },
  connecting: { bg: '#fff',       border: V2C.lineHard,  pillBg: V2C.brandSoft,pillFg: V2C.brand, label: 'Connecting…' },
};
export function V2StatusCard({ kind = 'none', icon, title, body, action }: {
  kind?: StatusKind; icon?: React.ReactNode; title: string; body?: React.ReactNode; action?: React.ReactNode
}) {
  const t = statusMap[kind];
  return (
    <div style={{
      background: t.bg, borderRadius: 16, border: `1px solid ${t.border}`,
      padding: 16, marginBottom: 12, transition: 'all 0.25s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: action ? 12 : 0 }}>
        {icon}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: body ? 4 : 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: V2C.ink, fontFamily: fontBody }}>{title}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.pillFg, background: t.pillBg, padding: '3px 8px', borderRadius: 99 }}>{t.label}</div>
          </div>
          {body && <div style={{ fontSize: 13, color: V2C.mute, lineHeight: 1.5 }}>{body}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
export function CheckIcon({ size = 14, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.5l3 3 6-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function ExclamationIcon({ size = 14, color = V2C.warn }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="6" stroke={color} strokeWidth="1.6" />
      <path d="M7 4v3.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="7" cy="10" r="0.9" fill={color} />
    </svg>
  );
}
export function ArrowRightIcon({ size = 14, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7h9m-3.5-3l3.5 3-3.5 3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function ArrowLeftIcon({ size = 14, color = V2C.mute }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ transform: 'rotate(180deg)' }}>
      <path d="M2.5 7h9m-3.5-3l3.5 3-3.5 3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function CopyIcon({ size = 14, color = V2C.mute }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="3.5" y="3.5" width="8" height="8" rx="1.5" stroke={color} strokeWidth="1.4" />
      <path d="M2 9V3a1.5 1.5 0 011.5-1.5H9" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
export function ExternalIcon({ size = 14, color = V2C.mute }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M5 3H3v8h8V9M8 2h4v4M12 2L7 7" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function SpinnerIcon({ size = 16, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ animation: 'v2spin 0.8s linear infinite' }}>
      <circle cx="8" cy="8" r="6" stroke={color} strokeWidth="2" strokeOpacity="0.25" />
      <path d="M8 2a6 6 0 016 6" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
export function GoogleGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18">
      <path fill="#EA4335" d="M9 3.7c1.4 0 2.6.5 3.6 1.4l2.6-2.6C13.5 1 11.4.2 9 .2 5.5.2 2.5 2.2 1 5.1l3 2.4C4.7 5.4 6.7 3.7 9 3.7z" />
      <path fill="#4285F4" d="M17.6 9.2c0-.7-.1-1.3-.2-1.9H9V11h4.9c-.2 1.1-.9 2.1-1.9 2.7l3 2.3c1.7-1.6 2.6-4 2.6-6.8z" />
      <path fill="#FBBC05" d="M4 10.5c-.2-.6-.3-1.2-.3-1.8s.1-1.2.3-1.8L1 4.5C.4 5.8 0 7.3 0 8.7s.4 2.9 1 4.2l3-2.4z" />
      <path fill="#34A853" d="M9 17.3c2.4 0 4.5-.8 6-2.2l-3-2.3c-.8.6-1.9.9-3 .9-2.3 0-4.3-1.7-5-3.8l-3 2.4c1.5 2.9 4.5 4.9 8 5z" />
    </svg>
  );
}

// ── Global keyframes injected once ───────────────────────────────────────────
let stylesInjected = false;
export function V2GlobalStyles() {
  if (typeof document === 'undefined') return null;
  if (!stylesInjected) {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes v2spin { to { transform: rotate(360deg); } }
      @keyframes v2slideInRight { from { opacity: 0; transform: translateX(32px); } to { opacity: 1; transform: translateX(0); } }
      @keyframes v2slideInLeft  { from { opacity: 0; transform: translateX(-32px); } to { opacity: 1; transform: translateX(0); } }
      @keyframes v2fadeIn       { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes v2bannerIn     { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes v2pulse        { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    `;
    document.head.appendChild(style);
    stylesInjected = true;
  }
  return null;
}
